/**
 * List Items API — personal shopping/to-do list items via Supabase.
 */

import supabase from "../config/supabase";

// UUID derived from the member and exact calendar occurrence. PostgreSQL's
// existing primary key makes simultaneous imports safe without a migration.
export async function calendarImportId(userId, notes) {
  const bytes = new Uint8Array(await window.crypto.subtle.digest("SHA-256",
    new TextEncoder().encode(JSON.stringify(["outback-calendar-v1", userId, notes]))));
  bytes[6] = (bytes[6] & 15) | 128; // UUID version 8, application-defined hash.
  bytes[8] = (bytes[8] & 63) | 128;
  const hex = Array.from(bytes.slice(0, 16), byte => byte.toString(16).padStart(2, "0")).join("");
  return `${hex.slice(0,8)}-${hex.slice(8,12)}-${hex.slice(12,16)}-${hex.slice(16,20)}-${hex.slice(20)}`;
}

const listItemsApi = {
  async list(kind) {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return { success: false, error: "Not authenticated" };

    // sort_order is the member's own order (051). created_at breaks ties, which
    // is what a run of brand new items looks like before any of them is moved.
    let query = supabase
      .from("list_items")
      .select("*, matches:list_item_matches(*)")
      .eq("user_id", user.id)
      .order("sort_order", { ascending: true })
      .order("created_at", { ascending: false });

    if (kind) query = query.eq("kind", kind);

    const { data, error } = await query;
    if (error) return { success: false, error: error.message };
    return { success: true, data: { items: data || [] } };
  },

  async create(body) {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return { success: false, error: "Not authenticated" };

    // priority + due_date are real columns (migration 007) — persist them so
    // To-Do metadata survives reload. Drop empty due_date so it stays NULL
    // rather than failing the DATE cast on "".
    // Explicit NULL bypasses the legacy database default without changing old rows.
    const insertBody = { ...body, priority: body.priority ?? null, user_id: user.id };
    if (!insertBody.due_date) delete insertBody.due_date;

    if (/^calendar:\d{4}-\d{2}-\d{2}:/.test(String(body.notes || ""))) {
      // Reuse rows imported by older versions, including completed ones.
      const existing = await supabase.from("list_items").select("*")
        .eq("user_id", user.id).eq("notes", body.notes)
        .order("created_at", {ascending: true}).limit(1).maybeSingle();
      if (existing.error) return {success: false, error: existing.error.message};
      if (existing.data) return {success: true, data: existing.data};
      try {
        insertBody.id = await calendarImportId(user.id, body.notes);
      } catch {
        return {success: false, error: "Couldn't safely import this calendar event. Please try again."};
      }
      const inserted = await supabase.from("list_items")
        .upsert(insertBody, {onConflict: "id", ignoreDuplicates: true}).select().maybeSingle();
      if (inserted.error) return {success: false, error: inserted.error.message};
      if (inserted.data) return {success: true, data: inserted.data};
      // Another tab/device won. Read its row; never reset its completion state.
      const winner = await supabase.from("list_items").select("*")
        .eq("user_id", user.id).eq("id", insertBody.id).single();
      return winner.error ? {success: false, error: winner.error.message} : {success: true, data: winner.data};
    }

    const { data, error } = await supabase
      .from("list_items")
      .insert(insertBody)
      .select()
      .single();

    if (error) return { success: false, error: error.message };
    return { success: true, data };
  },

  async update(itemId, body) {
    // Normalise empty DATE fields to NULL (empty string isn't a valid DATE).
    const updateBody = { ...body };
    if ("due_date" in updateBody && !updateBody.due_date) updateBody.due_date = null;
    if ("everyday_done_on" in updateBody && !updateBody.everyday_done_on) {
      updateBody.everyday_done_on = null;
    }

    const { data, error } = await supabase
      .from("list_items")
      .update(updateBody)
      .eq("id", itemId)
      .select()
      .single();

    if (error) return { success: false, error: error.message };
    return { success: true, data };
  },

  /**
   * Writes new positions for items the member has just moved past each other.
   * The arrows swap a pair, so this is normally two rows. One failure fails the
   * call, because half a swap leaves two items claiming the same spot and the
   * screen reloads to recover.
   */
  async setSortOrders(positions) {
    if (!positions?.length) return { success: true };

    const results = await Promise.all(
      positions.map(({ id, sort_order }) =>
        supabase.from("list_items").update({ sort_order }).eq("id", id),
      ),
    );

    const failed = results.find((r) => r.error);
    if (failed) return { success: false, error: failed.error.message };
    return { success: true };
  },

  async delete(itemId) {
    const { error } = await supabase.from("list_items").delete().eq("id", itemId);
    if (error) return { success: false, error: error.message };
    return { success: true };
  },

  async markMatchSeen(itemId, matchId) {
    const { error } = await supabase
      .from("list_item_matches")
      .update({ seen: true })
      .eq("id", matchId)
      .eq("list_item_id", itemId);

    if (error) return { success: false, error: error.message };
    return { success: true };
  },

  async markAllSeen() {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return { success: false, error: "Not authenticated" };

    const { data: userItems } = await supabase
      .from("list_items")
      .select("id")
      .eq("user_id", user.id);

    if (!userItems?.length) return { success: true };

    const itemIds = userItems.map((i) => i.id);
    const { error } = await supabase
      .from("list_item_matches")
      .update({ seen: true })
      .in("list_item_id", itemIds)
      .eq("seen", false);

    if (error) return { success: false, error: error.message };
    return { success: true };
  },

  async unseenCount() {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return { success: true, data: { count: 0 } };

    const { data: userItems } = await supabase
      .from("list_items")
      .select("id")
      .eq("user_id", user.id);

    if (!userItems?.length) return { success: true, data: { count: 0 } };

    const itemIds = userItems.map((i) => i.id);
    const { count, error } = await supabase
      .from("list_item_matches")
      .select("*", { count: "exact", head: true })
      .in("list_item_id", itemIds)
      .eq("seen", false);

    if (error) return { success: false, error: error.message };
    return { success: true, data: { count: count || 0 } };
  },
};

export default listItemsApi;
